import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { VersionService } from '#core/services/version.service';

/** One release of the app, as the gateway reads it from CHANGELOG.md */
export interface Release {
  version: string;
  /** YYYY-MM-DD */
  date: string;
  /** What is new, one sentence each */
  features: string[];
  /** What was fixed, one sentence each */
  fixes: string[];
  /** What the entry says in words instead of a list, if anything */
  note: string | null;
}

/** Remembered on this device: the version whose notes the user last opened */
const SEEN_KEY = 'whats-new.seen';

function readSeen(): string | null {
  try {
    return localStorage.getItem(SEEN_KEY);
  } catch {
    return null;
  }
}

/** What each version of the app changed, for the "What's new" page */
@Injectable({ providedIn: 'root' })
export class ChangelogService {
  private http = inject(HttpClient);
  private running = inject(VersionService).current;
  /** Kept here too, so opening the notes clears the dot even where the browser won't store it */
  private seen = signal(readSeen());

  /** The server runs a version whose notes the user hasn't opened on this device */
  readonly unseen = computed(() => {
    const version = this.running()?.version;
    return !!version && version !== this.seen();
  });

  /** The notes of the running version were opened */
  markSeen(): void {
    const version = this.running()?.version;
    if (!version || version === this.seen()) return;
    this.seen.set(version);
    try {
      localStorage.setItem(SEEN_KEY, version);
    } catch {
      // Not remembered: the dot is back after a reload
    }
  }

  /** The latest release first */
  releases(): Observable<Release[]> {
    return this.http.get<{ releases: Release[] }>('/api/changelog').pipe(map((answer) => answer.releases));
  }
}

/** A release's day as a date in the reader's own time zone (`new Date('2026-10-22')` would be UTC) */
export function releaseDay(release: Release): Date {
  const [year, month, day] = release.date.split('-').map(Number);
  return new Date(year, month - 1, day);
}
